///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { createReducer, on } from '@ngrx/store';
import { AccordionState, initialState } from './accordion.state';
import { getCustomersList, getSelectedCustomerInfo, setCustomersList, setSelectedCustomerInfo } from './accordion.actions';
import { blockCustomer, createCustomer, deleteCustomer, editCustomer } from '../../../customers-config/store/customer-config.actions';
import { CustomerDTO } from '../../../generated/shared-types.model';
import {
    createProject,
    deleteProject,
    moveProject
} from '../../../projects/dialogs/store/project-modal-management/project-modal-management.actions';
import { userCreationHandling, userDeletionHandling } from '../../../users/store/users-list.actions';

export const sortCustomers = (customers: CustomerDTO[], param: string): CustomerDTO[] => {
    return customers.sort((a, b) => a[param].toLowerCase().localeCompare(b[param].toLowerCase()));
};

export const reducer = createReducer(
    initialState,
    on(getCustomersList, (state: AccordionState): AccordionState => {
        return {
            ...state,
            isLoading: true
        };
    }),

    on(setCustomersList, (state: AccordionState, action): AccordionState => {
        const sortedList = sortCustomers([...action.customersList], 'name');
        return {
            ...state,
            isLoading: false,
            customersList: sortedList
        };
    }),

    on(getSelectedCustomerInfo, (state: AccordionState, action): AccordionState => {
        return {
            ...state,
            selectedCustomerId: action.selectedCustomerId
        };
    }),

    on(setSelectedCustomerInfo, (state: AccordionState, action): AccordionState => {
        return {
            ...state,
            selectedCustomerId: action?.customer?.id,
            currentCustomer: action.customer
        };
    }),

    on(userCreationHandling, (state: AccordionState): AccordionState => {
        const selectedCustomerId = state.selectedCustomerId;

        if (!state.customersList) {
            return;
        } // we need this condition if login like user (we don't have accordion in this case)

        const customersListWithAddedUser = state.customersList.map((customer) => {
            return customer.id === selectedCustomerId ? { ...customer, numberOfUsers: customer.numberOfUsers + 1 } : customer;
        });

        return {
            ...state,
            customersList: customersListWithAddedUser,
            currentCustomer: { ...state.currentCustomer, numberOfUsers: state.currentCustomer.numberOfUsers + 1 }
        };
    }),

    on(userDeletionHandling, (state: AccordionState): AccordionState => {
        const selectedCustomerId = state.selectedCustomerId;

        if (!state.customersList) {
            return;
        } // we need this condition if login like user (we don't have accordion in this case)

        const customersListWithDeletedUser = state.customersList.map((customer) => {
            return customer.id === selectedCustomerId ? { ...customer, numberOfUsers: customer.numberOfUsers - 1 } : customer;
        });

        return {
            ...state,
            customersList: customersListWithDeletedUser,
            currentCustomer: { ...state.currentCustomer, numberOfUsers: state.currentCustomer.numberOfUsers - 1 }
        };
    }),

    on(createCustomer, (state: AccordionState, action): AccordionState => {
        const listWithNewCustomer = sortCustomers([...state.customersList, action.newCustomer], 'name');

        return {
            ...state,
            customersList: listWithNewCustomer
        };
    }),

    on(blockCustomer, (state: AccordionState, action): AccordionState => {
        const listWithNewCustomer = state.customersList.map((customer) => {
            if (customer.id !== action.id) {
                return customer;
            }

            return {
                ...customer,
                blocked: !customer.blocked
            };
        });

        return {
            ...state,
            customersList: listWithNewCustomer
        };
    }),

    on(editCustomer, (state: AccordionState, action): AccordionState => {
        const listWithNewCustomer = state.customersList.map((customer) =>
            customer.id === action.updatedCustomer.id ? action.updatedCustomer : customer
        );

        return {
            ...state,
            customersList: listWithNewCustomer
        };
    }),

    on(deleteCustomer, (state: AccordionState, action): AccordionState => {
        const listWithoutDeletedCustomer = state.customersList.filter((customer) => customer.id !== action.id);

        return {
            ...state,
            customersList: listWithoutDeletedCustomer
        };
    }),

    on(createProject, (state: AccordionState): AccordionState => {
        const selectedCustomerId = state.selectedCustomerId;

        if (!state.customersList) {
            return;
        } // we need this condition if login like user (we don't have accordion in this case)

        const customersListWithDeletedUser = state.customersList.map((customer) => {
            return customer.id === selectedCustomerId ? { ...customer, numberOfProjects: customer.numberOfProjects + 1 } : customer;
        });

        return {
            ...state,
            customersList: customersListWithDeletedUser,
            currentCustomer: { ...state.currentCustomer, numberOfProjects: state.currentCustomer.numberOfProjects + 1 }
        };
    }),

    on(deleteProject, (state: AccordionState): AccordionState => {
        const selectedCustomerId = state.selectedCustomerId;

        if (!state.customersList) {
            return;
        } // we need this condition if login like user (we don't have accordion in this case)

        const customersListWithDeletedUser = state.customersList.map((customer) => {
            return customer.id === selectedCustomerId ? { ...customer, numberOfProjects: customer.numberOfProjects - 1 } : customer;
        });

        return {
            ...state,
            customersList: customersListWithDeletedUser,
            currentCustomer: { ...state.currentCustomer, numberOfProjects: state.currentCustomer.numberOfProjects - 1 }
        };
    }),

    on(moveProject, (state: AccordionState, action): AccordionState => {
        if (!state.customersList) {
            return;
        }

        const selectedCustomerId = state.selectedCustomerId;
        const targetCustomerId = action.targetCustomerId;

        const updatedCustomersList = state.customersList.map((customer) => {
            if (customer.id === selectedCustomerId) {
                return { ...customer, numberOfProjects: customer.numberOfProjects - 1 };
            } else if (customer.id === targetCustomerId) {
                return { ...customer, numberOfProjects: customer.numberOfProjects + 1 };
            }
            return customer;
        });

        return {
            ...state,
            customersList: updatedCustomersList,
            currentCustomer: { ...state.currentCustomer, numberOfProjects: state.currentCustomer.numberOfProjects - 1 }
        };
    })
);
